import React from 'react'

import { Redirect, useLocation, useParams } from 'react-router'

import { DefaultLayoutComponent, isNewLayoutApp } from './layout'

import {
  PerformanceReport,
  PerformanceReportState,
} from '../components/PerformanceReport'

import { DataWarehouseProvider, DataWarehouseState } from 'components/DataWarehouse'

import { intersection, noop } from 'lodash-es'

import { parseUrlSearchParams, stringifyUrlSearchParams } from '@percept/hooks'


export const PerformanceReportScreen = (): JSX.Element => {
  return (
    <DefaultLayoutComponent>
      <PerformanceReport />
    </DefaultLayoutComponent>
  )
}

const getDataWarehouseReportType = ({ segmentation, providers }: PerformanceReportState): string | undefined => {
  // Most cross-provider reports with ambiguous names in the old Performance Report view are
  // now a lot more specialised, meaning we can't easily derive certain combinations as they
  // are no longer valid. We match what we can where we know the providers selected map to a
  // known report type from the earlier more vague segmentations.
  const isSingleProvider = providers.length === 1
  switch(segmentation){
    case 'campaign_objective': return 'CAMPAIGN_OBJECTIVE'
    case 'network': {
      if( providers.length <= 2 && intersection(providers, ['GOOGLE_ADS', 'FACEBOOK']).length === providers.length ){
        return 'NETWORK'
      }
      break
    }
    case 'sub_network': {
      if( isSingleProvider && providers[0] === 'GOOGLE_ADS' ){
        return 'CHANNEL'
      }
      if( providers.length <= 2 && intersection(providers, ['TIKTOK', 'FACEBOOK']).length === providers.length ){
        return 'PLACEMENT'
      }
      break
    }
    default: return 'UNSEGMENTED'
  }
  return 'UNSEGMENTED'
}

const getDataWarehouseUrlState = (urlState: PerformanceReportState): DataWarehouseState => {
  const dateRangePreset = urlState.dateRangePreset || 'custom'
  return {
    reportType: getDataWarehouseReportType(urlState),
    // Amazon is not yet supported by DWH
    providers: (urlState.providers || []).filter( p => p !== 'AMAZON_ADS' ) as DataWarehouseProvider[],
    start: dateRangePreset === 'custom' ? urlState.start : undefined,
    end: dateRangePreset === 'custom' ? urlState.end : undefined,
    dateRangePreset,
    organisations: urlState.organisations || [],
    currency: urlState.currency,
  }
}

export const PerformanceReportingRedirect = (): JSX.Element => {
  const { org_unit_id } = useParams<{ org_unit_id: string }>()
  const location = useLocation()
  let search: string | undefined = undefined
  try{
    const searchParams = parseUrlSearchParams(location.search) as PerformanceReportState
    const dataWarehouseUrlState = getDataWarehouseUrlState(searchParams)
    search = stringifyUrlSearchParams(dataWarehouseUrlState)
  }catch(e){
    noop()
  }
  return (
    <Redirect
      push={false}
      to={{
        pathname: `/dashboards/${org_unit_id}/data-warehouse`,
        search,
      }} />
  )
}

export default (
  isNewLayoutApp ?
    PerformanceReportingRedirect :
    PerformanceReportScreen
)
